import { useEffect } from 'react';

import { useAtomValue } from 'jotai';

import {
  isRecordingSessionActiveAtom,
  isUploadInProgressAtom,
} from '../atoms/recording';
import { isNoteGenerationActiveAtom } from '../atoms/ui';

/**
 * Hook that warns users when attempting to refresh or close the browser tab
 * while a recording, upload, or note generation is in progress.
 *
 * Installs a `beforeunload` handler so that full-page navigations, refreshes,
 * and tab closes trigger the browser's native "Changes may not be saved" dialog.
 *
 * Note: Modern browsers show a generic message rather than custom text
 * for security reasons.
 */
export const useNavigationBlock = () => {
  const isRecordingSessionActive = useAtomValue(isRecordingSessionActiveAtom);
  const isUploadInProgress = useAtomValue(isUploadInProgressAtom);
  const isNoteGenerationActive = useAtomValue(isNoteGenerationActiveAtom);

  const shouldBlock =
    isRecordingSessionActive || isUploadInProgress || isNoteGenerationActive;

  useEffect(() => {
    if (!shouldBlock) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock]);

  return { shouldBlock };
};
